import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Pastille } from '../ui/Primitives';
import type { ApercuFacturation } from '../../lib/useFinancements';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/**
 * Ce que diront les factures d'un contrat (TF-406) ou d'un accord de
 * financement OF (TF-417) : le client et les mentions que les financeurs
 * exigent, tels que l'émission les recopiera. Payeur et référence sont
 * au-dessus ; ici, ce qu'ils deviennent sur la facture, et ce qui manque encore
 * pour l'adresser — la liste même que l'émission refusera.
 */
export function ApercuFactures({
  apercu,
  libelleApprenant = 'Apprenti(e)',
}: {
  apercu: ApercuFacturation;
  libelleApprenant?: string;
}): JSX.Element {
  const { client } = apercu;
  return (
    <section className="rounded-2xl border border-gray-100" aria-label="Données portées sur les factures">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
        <h5 className="text-sm font-bold text-codialis-navy">Données portées sur les factures</h5>
        <Pastille
          texte={apercu.prete ? 'Prêtes à facturer' : 'À compléter'}
          ton={apercu.prete ? 'success' : 'warning'}
        />
      </div>
      {apercu.detail && (
        <p className="mx-3 mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 sm:mx-4">
          {apercu.detail}
        </p>
      )}
      <dl className="grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 sm:p-4">
        <Rubrique libelle="Client facturé">
          {client ? (
            <>
              <span className="break-words">{client.nom}</span>
              <span className="block text-[11px] font-normal text-codialis-gray">
                {client.typeLibelle} · règlement à {client.delaiPaiementJours} jours
              </span>
              {client.adresse && (
                <span className="mt-1 block whitespace-pre-line break-words font-normal">{client.adresse}</span>
              )}
              <span className="block font-mono text-[11px] font-normal text-codialis-gray">
                {client.siret ? `SIRET ${client.siret}` : 'SIRET manquant'}
              </span>
              {client.detail && (
                <span className="mt-1 block text-[11px] font-semibold text-amber-700">
                  {client.detail}{' '}
                  {client.fiche.nature === 'FINANCEUR' ? (
                    <Link
                      to={`/financing?financeur=${encodeURIComponent(client.fiche.id)}`}
                      className="inline-flex min-h-[32px] items-center text-codialis-sky underline-offset-2 hover:underline"
                    >
                      Compléter la fiche du financeur
                    </Link>
                  ) : (
                    <span className="font-normal text-codialis-gray">
                      La fiche de l’entreprise se complète au répertoire, par la relation entreprise ou
                      l’administratif.
                    </span>
                  )}
                </span>
              )}
            </>
          ) : (
            <span className="text-amber-700">Payeur à désigner</span>
          )}
        </Rubrique>
        <Rubrique libelle={libelleApprenant}>{apercu.apprenant}</Rubrique>
        <Rubrique libelle="Formation">
          <span className="break-words">{apercu.formation ?? '—'}</span>
        </Rubrique>
        <Rubrique libelle="Dates de formation">
          {apercu.datesFormation.texte ?? <span className="text-amber-700">À préciser</span>}
          {apercu.datesFormation.provenance && (
            <span className="block text-[11px] font-normal text-codialis-gray">
              {apercu.datesFormation.provenance}
            </span>
          )}
        </Rubrique>
        <Rubrique libelle={`Heures réalisées au ${jourFrancais(apercu.heures.jusquA)}`}>
          {apercu.heures.texte}
          <span className="block text-[11px] font-normal text-codialis-gray">
            Lues sur : {apercu.heures.provenance}. Chaque facture dira aussi les heures de sa période.
          </span>
          {apercu.heures.avertissement && (
            <span className="block text-[11px] font-semibold text-amber-700">{apercu.heures.avertissement}</span>
          )}
        </Rubrique>
        {apercu.numeroProForma && (
          <Rubrique libelle="N° de pro forma">
            <span className="break-all font-mono">{apercu.numeroProForma}</span>
            <span className="block text-[11px] font-normal text-codialis-gray">
              Imprimé sous la référence de prise en charge.
            </span>
          </Rubrique>
        )}
      </dl>
    </section>
  );
}

export function Rubrique({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</dt>
      <dd className="mt-1 text-sm font-medium text-codialis-navy">{children}</dd>
    </div>
  );
}
